import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Grid } from '@mui/material';
import { Helmet } from 'react-helmet';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useTheme, useMediaQuery } from '@mui/material';

// --- Import des images ---
import slider1 from '../assets/slider1.png';
import slider2 from '../assets/slider2.png';
import slider3 from '../assets/slider3.png';
import slider4 from '../assets/slider4.png';
import slider5 from '../assets/slider5.png';
import slider6 from '../assets/slider6.png';
import slider7 from '../assets/slider7.png';
import slider8 from '../assets/slider8.png';
import slider9 from '../assets/slider9.png';
import slider10 from '../assets/slider10.png';
import image from '../assets/image.png';
import image1 from '../assets/image1.png';
import image2 from '../assets/image2.png';
import image3 from '../assets/image3.png';
import image4 from '../assets/image4.png';
import image5 from '../assets/image5.png';
import image6 from '../assets/image6.png';
import image7 from '../assets/image7.png';
import image8 from '../assets/image8.png';
import image10 from '../assets/image10.png';

function CustomerArea() {
  const { t } = useTranslation();
  const sliderImages = [slider1, slider2, slider3, slider4, slider5, slider6, slider7, slider8, slider9, slider10];
  const [currentIndex, setCurrentIndex] = useState(0);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('md'));

  const transitionDuration = 1500; // 1.5s en ms
  const displayTime = 5000; // 5s de visibilité

  // Données clients (traductions via t())
  const clients = [
    { img: image8, desc: t('clients.coficab') },
    { img: image5, desc: t('clients.mta') },
    { img: image4, desc: t('clients.nexteer') },
    { img: image1, desc: t('clients.soverep') },
    { img: image7, desc: t('clients.plasticPlating') },
    { img: image, desc: t('clients.imGears') },
    { img: image2, desc: t('clients.magna') },
    { img: image6, desc: t('clients.stellantic') },
    { img: image3, desc: t('clients.xpo') },
    { img: slider10, desc: t('clients.dhl') },
    { img: image10, desc: t('clients.plasticum') },
  ];

  // Changement automatique du slider (uniquement pour desktop)
  useEffect(() => {
    if (!isXs) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % sliderImages.length);
      }, displayTime + transitionDuration);
      return () => clearInterval(interval);
    }
  }, [isXs, sliderImages.length, displayTime, transitionDuration]);

  return (
    <>
      <Box sx={{ my: 9 }}>
        <Helmet>
          <title>SPEEDY Transport & Logistics</title>
        </Helmet>

        {/* Section Hero avec image animée */}
        <Box
          sx={{
            position: 'relative',
            height: { xs: '80vh', sm: '80vh', md: '50vh' }, // hauteur variable selon écran
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'flex-start', md: 'flex-start' }, // centré sur mobile, à gauche sur PC
            color: 'white',
            textAlign: { xs: 'center', md: 'left' },
          }}
        >
          {/* --- SECTION SLIDER --- */}
          {isXs ? (
            // ✅ Sur mobile : effet rideau continu corrigé
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                overflow: 'hidden',
                display: 'flex',
              }}
            >
              <motion.div
                style={{
                  display: 'flex',
                  width: `${sliderImages.length * 10 * 100}vw`, // Largeur totale pour 20 images (10 originales + 10 dupliquées)
                }}
                animate={{
                  x: ['0vw', '-5000vw'], // Défilement pour montrer les 10 images, puis recommence avec les dupliquées
                }}
                transition={{
                  repeat: Infinity,
                  ease: 'linear',
                  duration: 80, // Ajuste la vitesse si nécessaire
                }}
              >
                {/* ✅ On double le tableau pour effet continu */}
                {[...sliderImages, ...sliderImages].map((img, i) => (
                  <Box
                    key={i}
                    sx={{
                      width: '450vw', // Chaque image prend toute la largeur de l'écran
                      backgroundImage: `url(${img})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      height: '100%',
                      flexShrink: 0, // Empêche le rétrécissement
                    }}
                  />
                ))}
              </motion.div>
            </Box>
          ) : (
            // 💻 Sur desktop : version image par image avec AnimatePresence
            <AnimatePresence>
              <motion.div
                key={currentIndex}
                initial={{
                  opacity: 0,
                  x: [3, 6, 7].includes(currentIndex) ? '100%' : '-100%', // sliders 4,7,8 viennent de droite
                }}
                animate={{
                  opacity: 1,
                  x: 0,
                  transition: { duration: 1.5, ease: 'easeInOut' },
                }}
                exit={{
                  opacity: 0,
                  x: [3, 6, 7].includes(currentIndex) ? '-100%' : '100%',
                  transition: { duration: 1.5, ease: 'easeInOut' },
                }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundImage: `url(${sliderImages[currentIndex]})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  zIndex: 0,
                }}
              />
            </AnimatePresence>
          )}

          {/* --- Bloc demi-cercle --- */}
          <motion.div
            initial={{ x: -400, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 40, damping: 20 }}
            style={{ position: 'relative', zIndex: 2 }}
          >
            <Box
              sx={{
                width: { xs: '400px', sm: '400px', md: '300px', lg: '480px' },
                height: { xs: '160px', sm: '160px', md: '420px', lg: '450px' },
                background: 'linear-gradient(135deg, rgba(30,30,30,0.85), rgba(70,70,70,0.85))',
                borderRadius: '0 50% 50% 0',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                pl: { xs: 3, sm: 5, md: 8 },
                ml: { xs: -1, sm: -1, md: -2 },
                textAlign: 'left',
                backdropFilter: 'blur(4px)',
                mt: { xs: -25, sm: -25, md: 0 },
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  color: '#fff',
                  fontWeight: 800,
                  mb: 1,
                  fontSize: { xs: '1.8rem', sm: '2.2rem', md: '3rem' },
                }}
              >
                {t('customers.title')}
              </Typography>
              <Box
                sx={{
                  width: { xs: '40px', sm: '50px' },
                  height: '10px',
                  backgroundColor: '#ffb703',
                  mb: 2,
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  color: 'rgba(255,255,255,0.9)',
                  fontSize: { xs: '0.9rem', sm: '1rem', md: '1.1rem' },
                  maxWidth: { xs: '90%', sm: '80%', md: '70%' },
                }}
              >
                {t('customers.subtitle')}
              </Typography>
            </Box>
          </motion.div>
        </Box>

        {/* Section clients */}
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              textAlign: 'center',
              fontWeight: 900,
              color: '#222',
              mb: 1,
              fontFamily: "'Poppins', sans-serif",
              textTransform: 'uppercase',
              letterSpacing: '1px',
            }}
          >
            {t('customers.trustTitle')}
          </Typography>

          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              color: 'text.secondary',
              maxWidth: '600px',
              mx: 'auto',
              mb: 6,
              fontFamily: "'Roboto', sans-serif",
              lineHeight: 1.8,
            }}
          >
            {t('customers.trustText')}
          </Typography>

          <Grid container spacing={4} justifyContent="center" alignItems="stretch">
            {clients.map((client, index) => (
              <Grid item xs={1} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: 4,
                    boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
                    p: 4,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: '140px',
                      height: '120px',
                      borderRadius: '50%',
                      overflow: 'hidden',
                      mb: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f7f7f7',
                    }}
                  >
                    <img
                      src={client.img}
                      alt={`Client ${index + 1}`}
                      style={{
                        width: '80%',
                        height: 'auto',
                        objectFit: 'contain',
                      }}
                    />
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{
                      color: '#333',
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 600,
                      mb: 1,
                      fontSize: '1.3rem',
                    }}
                  >
                    {client.desc.split(':')[0]}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontFamily: "'Roboto', sans-serif",
                      lineHeight: 1.5,
                    }}
                  >
                    {client.desc.split(':')[1]}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </>
  );
}

export default CustomerArea;
